//src/components/LoginForm.js
import React, { useState } from "react";
import {
    Card, CardContent, TextField, Button, Checkbox, FormControlLabel,
    CircularProgress, InputAdornment, IconButton, Box, Typography, Fade,
    Dialog, DialogTitle, DialogContent, DialogActions, Tabs, Tab, Alert, Link
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import LockIcon from "@mui/icons-material/Lock";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import Logo from "../assets/sigifblue.svg";
import { loginJWT, solicitarResetPublic } from "../api/endpoints";
import "../styles/loginForm.css";

// Solo guardamos el USUARIO (no la contraseña). Es el comportamiento estándar
// de "recordarme" en sistemas serios — la contraseña nunca toca localStorage.
const REMEMBER_KEY = 'SIGIF_REMEMBER_USER';

function LoginForm({ setUser, setSessionExpired }) {
    const recordadoInicial = (() => {
        try { return localStorage.getItem(REMEMBER_KEY) || ""; } catch { return ""; }
    })();
    const [usuario, setUsuario] = useState(recordadoInicial);
    const [password, setPassword] = useState("");
    const [recordar, setRecordar] = useState(!!recordadoInicial);
    const [showPass, setShowPass] = useState(false);
    const [mensaje, setMensaje] = useState("");
    const [cargando, setCargando] = useState(false);

    const [userError, setUserError] = useState("");
    const [passError, setPassError] = useState("");
    const [shakeUser, setShakeUser] = useState(false);
    const [shakePass, setShakePass] = useState(false);

    // Dialog "Olvide mi contrasena/usuario"
    const [resetOpen, setResetOpen] = useState(false);
    const [resetTab, setResetTab] = useState(0); // 0 = password, 1 = usuario
    const [resetInput, setResetInput] = useState("");
    const [resetEnviando, setResetEnviando] = useState(false);
    const [resetResultado, setResetResultado] = useState(null); // { ok, mensaje }

    const abrirReset = (tabInicial = 0) => {
        setResetTab(tabInicial);
        setResetInput("");
        setResetResultado(null);
        setResetOpen(true);
    };
    const cerrarReset = () => {
        setResetOpen(false);
    };
    const enviarReset = async () => {
        const valor = (resetInput || "").trim();
        if (!valor) return;
        setResetEnviando(true);
        try {
            const tipo = resetTab === 0 ? "PASSWORD" : "USUARIO";
            const resp = await solicitarResetPublic({ tipo, dniOrUsuario: valor });
            // El backend siempre devuelve mensaje generico; igual lo mostramos.
            setResetResultado({
                ok: resp?.ok !== false,
                mensaje: resp?.mensaje ||
                    "Si los datos coinciden con un usuario registrado, los administradores recibiran tu solicitud y se comunicaran por canal externo.",
            });
        } catch {
            setResetResultado({
                ok: false,
                mensaje: "No se pudo enviar la solicitud. Intentalo de nuevo en unos minutos.",
            });
        } finally {
            setResetEnviando(false);
        }
    };

    const year = new Date().getFullYear();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMensaje("");
        setUserError("");
        setPassError("");

        const u = (usuario || "").trim();
        const p = (password || "").trim();

        let hasError = false;
        if (!u) { setUserError("Ingresá tu usuario."); setShakeUser(true); hasError = true; }
        if (!p) { setPassError("Ingresá tu contraseña."); setShakePass(true); hasError = true; }
        if (hasError) {
            setTimeout(() => { setShakeUser(false); setShakePass(false); }, 420);
            return;
        }
        setCargando(true);
        try {
            const data = await loginJWT({ usuario: u, password: p });
            setCargando(false);

            if (data?.ok) {
                // Persistir o limpiar el usuario recordado SOLO al loguearse OK,
                // así no guardamos datos de un usuario que falló validación.
                try {
                    if (recordar) localStorage.setItem(REMEMBER_KEY, u);
                    else localStorage.removeItem(REMEMBER_KEY);
                } catch { /* localStorage puede estar bloqueado */ }
                setUser(data.usuario);
            } else {
                setMensaje(data?.mensaje || "Usuario o contraseña incorrectos.");
            }
        } catch (err) {
            setCargando(false);
            setMensaje(err?.message || "Error al conectar con el servidor.");
        }
    };

    return (
        <>
            <Box className="bg-anim login-container">
                <Fade in>
                    <Card elevation={0} className="login-card">
                        <CardContent style={{ padding: "26px 24px 24px 24px" }}>
                            <div className="login-grid">
                                {/* Columna izquierda: LOGO */}
                                <div className="login-left">
                                    <img src={Logo} alt="SIGIF Logo" className="logo" />
                                </div>

                                {/* Columna derecha: FORM (bloque interno) */}
                                <div className="login-right">
                                    <div className="form-panel">
                                        <form onSubmit={handleSubmit} autoComplete="off">
                                            {/* Usuario */}
                                            <div className={`field-block ${shakeUser ? "field-shake" : ""}`}>
                                                <TextField
                                                    className="pill-input helper-tight"
                                                    placeholder="Usuario"
                                                    variant="outlined"
                                                    fullWidth
                                                    value={usuario}
                                                    onChange={e => { setUsuario(e.target.value); if (userError) setUserError(""); }}
                                                    margin="dense"
                                                    error={!!userError}
                                                    helperText={userError || " "}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <PersonIcon />
                                                            </InputAdornment>
                                                        )
                                                    }}
                                                    InputLabelProps={{ shrink: false }}
                                                />
                                            </div>

                                            {/* Contraseña */}
                                            <div className={`field-block ${shakePass ? "field-shake" : ""}`}>
                                                <TextField
                                                    className="pill-input helper-tight"
                                                    placeholder="Contraseña"
                                                    variant="outlined"
                                                    fullWidth
                                                    value={password}
                                                    type={showPass ? "text" : "password"}
                                                    onChange={e => { setPassword(e.target.value); if (passError) setPassError(""); }}
                                                    margin="dense"
                                                    error={!!passError}
                                                    helperText={passError || " "}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <LockIcon />
                                                            </InputAdornment>
                                                        ),
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <IconButton onClick={() => setShowPass(s => !s)} size="small" tabIndex={-1}>
                                                                    {showPass ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        )
                                                    }}
                                                    InputLabelProps={{ shrink: false }}
                                                />
                                            </div>

                                            {/* Recordar usuario */}
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={recordar}
                                                        onChange={(e) => setRecordar(e.target.checked)}
                                                        size="small"
                                                        sx={{ color: '#2776b6', '&.Mui-checked': { color: '#2776b6' } }}
                                                    />
                                                }
                                                label={<span style={{ fontSize: 13, color: '#555' }}>Recordar mi usuario en este equipo</span>}
                                                sx={{ ml: 0, mb: 0.5 }}
                                            />

                                            {/* Botón */}
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                fullWidth
                                                type="submit"
                                                disabled={cargando}
                                                className="btn-pill"
                                            >
                                                {cargando ? <CircularProgress size={22} color="inherit" /> : "INGRESAR"}
                                            </Button>

                                            {/* Mensaje general (credenciales incorrectas / red) */}
                                            {mensaje && (
                                                <Typography variant="body2" color="error" align="center" style={{ marginTop: 10 }}>
                                                    {mensaje}
                                                </Typography>
                                            )}

                                            {/* Links de recuperacion */}
                                            <Box sx={{ mt: 1.5, textAlign: 'center' }}>
                                                <Link
                                                    component="button"
                                                    type="button"
                                                    onClick={() => abrirReset(0)}
                                                    underline="hover"
                                                    sx={{ fontSize: 13, color: '#2776b6', mr: 1.5 }}
                                                >
                                                    ¿Olvidaste tu contraseña?
                                                </Link>
                                                <Typography component="span" sx={{ fontSize: 13, color: '#bbb' }}>·</Typography>
                                                <Link
                                                    component="button"
                                                    type="button"
                                                    onClick={() => abrirReset(1)}
                                                    underline="hover"
                                                    sx={{ fontSize: 13, color: '#2776b6', ml: 1.5 }}
                                                >
                                                    ¿Olvidaste tu usuario?
                                                </Link>
                                            </Box>
                                        </form>
                                    </div>
                                </div>
                            </div>

                            {/* Divider + leyenda */}
                            <div className="sigif-divider" />
                            <div className="login-legend">
                                <Typography variant="h6" style={{ color: "#2776b6", fontWeight: 600 }}>
                                    Fiscalía de Estado
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Provincia de San Juan
                                </Typography>
                            </div>
                        </CardContent>
                    </Card>
                </Fade>

                {/* Footer fijo */}
                <Box
                    position="fixed"
                    bottom={0}
                    width="100%"
                    p={1.3}
                    style={{
                        background: "rgba(25,50,90,0.15)",
                        textAlign: "center",
                        fontSize: 15,
                        color: "#ffffffcc",
                        letterSpacing: 0.2,
                        zIndex: 1300
                    }}
                >
                    Dirección de Informática - Fiscalía de Estado © {year}
                </Box>
            </Box>

            {/* Dialog: Recuperar contraseña / usuario */}
            <Dialog
                open={resetOpen}
                onClose={resetEnviando ? undefined : cerrarReset}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: '4px' } }}
            >
                <DialogTitle sx={{
                    background: 'linear-gradient(135deg, #2776b6 0%, #1565c0 100%)',
                    color: '#fff',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    pb: 1.5,
                }}>
                    <HelpOutlineIcon /> Recuperar acceso
                </DialogTitle>
                <DialogContent sx={{ pt: '16px !important' }}>
                    <Tabs
                        value={resetTab}
                        onChange={(_, v) => { setResetTab(v); setResetResultado(null); }}
                        variant="fullWidth"
                        sx={{ mb: 2, borderBottom: '1px solid #e0e0e0' }}
                    >
                        <Tab label="Olvidé contraseña" />
                        <Tab label="Olvidé usuario" />
                    </Tabs>

                    {resetResultado ? (
                        <Alert severity={resetResultado.ok ? 'success' : 'error'} sx={{ mb: 1 }}>
                            {resetResultado.mensaje}
                        </Alert>
                    ) : (
                        <>
                            <Typography variant="body2" sx={{ color: '#555', mb: 1.5 }}>
                                {resetTab === 0
                                    ? 'Ingresá tu DNI o tu usuario. Un administrador procesará tu solicitud y se comunicará con vos por canal externo.'
                                    : 'Ingresá tu DNI. Un administrador te comunicará tu usuario por canal externo.'}
                            </Typography>
                            <TextField
                                autoFocus
                                fullWidth
                                size="small"
                                label={resetTab === 0 ? 'DNI o usuario' : 'DNI'}
                                value={resetInput}
                                onChange={e => setResetInput(e.target.value)}
                                disabled={resetEnviando}
                                onKeyDown={e => { if (e.key === 'Enter') enviarReset(); }}
                            />
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={cerrarReset} disabled={resetEnviando} sx={{ textTransform: 'none' }}>
                        {resetResultado ? 'Cerrar' : 'Cancelar'}
                    </Button>
                    {!resetResultado && (
                        <Button
                            variant="contained"
                            onClick={enviarReset}
                            disabled={resetEnviando || !(resetInput || '').trim()}
                            sx={{ textTransform: 'none' }}
                            startIcon={resetEnviando ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                            Enviar solicitud
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
}

export default LoginForm;
